import type {
  CSVExporter,
  ExportResult,
  ValidationResult,
  MarketplaceRequirements,
  MarketplaceType,
} from '../contracts/csvExporter'
import { ExportError, EXPORT_CODES } from '../contracts/csvExporter'
import type { Batch } from '../contracts/storageService'

export class MarketplaceCSVExporter implements CSVExporter {
  private marketplaceRequirements: Record<MarketplaceType, MarketplaceRequirements> = {
    shutterstock: {
      requiredColumns: ['Filename', 'Description', 'Keywords', 'Categories', 'Editorial', 'Mature content', 'illustration'],
      optionalColumns: [],
      maxDescriptionLength: 200,
      maxKeywords: 50,
      keywordSeparator: ',',
      csvDelimiter: ',',
      requiresHeader: true,
      filenameFormat: 'original',
    },
    'adobe-stock': {
      requiredColumns: ['Filename', 'Title', 'Keywords', 'Category', 'Releases'],
      optionalColumns: [],
      maxDescriptionLength: 2000, // Not used, Title serves as description
      maxTitleLength: 200,
      maxKeywords: 49,
      keywordSeparator: ', ', // Adobe Stock uses comma-space separator
      csvDelimiter: ',',
      requiresHeader: true,
      filenameFormat: 'original', // Adobe Stock requires filename with extension
    },
    istock: {
      requiredColumns: ['filename', 'description', 'keywords'],
      optionalColumns: ['title', 'category'],
      maxDescriptionLength: 1500,
      maxKeywords: 50,
      keywordSeparator: ',',
      csvDelimiter: ',',
      requiresHeader: true,
      filenameFormat: 'original',
    },
    pond5: {
      requiredColumns: ['File Name', 'Title', 'Description', 'Keywords'],
      optionalColumns: ['Category', 'Model Released', 'Property Released'],
      maxDescriptionLength: 2500,
      maxKeywords: 40,
      keywordSeparator: ' ',
      csvDelimiter: ',',
      requiresHeader: true,
      filenameFormat: 'original',
    },
  }

  async exportToMarketplace(batch: Batch, marketplace: MarketplaceType): Promise<ExportResult> {
    try {
      const requirements = this.marketplaceRequirements[marketplace]

      // Validate batch first
      const validation = this.validateExport(batch, marketplace)
      if (!validation.isValid) {
        throw new ExportError(
          `Export validation failed: ${validation.errors.map(e => e.message).join(', ')}`,
          EXPORT_CODES.VALIDATION_FAILED,
          marketplace,
          { errors: validation.errors },
        )
      }

      const csvContent = this.generateCSVContent(batch, marketplace)
      const filename = `${batch.name || `batch-${batch.id}`}-${marketplace}-export.csv`

      return {
        marketplace,
        filename,
        content: csvContent,
        rowCount: batch.images.length,
        exportedAt: new Date(),
        requirements: requirements || {
          requiredColumns: ['Filename', 'Title', 'Keywords'],
          optionalColumns: [],
          maxDescriptionLength: 2000,
          maxKeywords: 50,
          keywordSeparator: ',',
          csvDelimiter: ',',
          requiresHeader: true,
          filenameFormat: 'original',
        },
      }
    } catch (error) {
      if (error instanceof ExportError) {
        throw error
      }
      throw new ExportError(
        'Failed to export to marketplace',
        EXPORT_CODES.EXPORT_GENERATION_FAILED,
        marketplace,
        { originalError: error instanceof Error ? error.message : String(error) },
      )
    }
  }

  validateExport(batch: Batch, marketplace: MarketplaceType): ValidationResult {
    const requirements = this.marketplaceRequirements[marketplace]
    const errors: ValidationResult['errors'] = []
    const warnings: ValidationResult['warnings'] = []

    // Validate batch has images
    if (!batch.images || batch.images.length === 0) {
      errors.push({
        code: 'NO_IMAGES',
        message: 'Batch contains no images to export',
        severity: 'error',
      })
      return { isValid: false, errors, warnings }
    }

    // Validate each image
    batch.images.forEach((image, index) => {
      const metadata = image.metadata
      if (!metadata) {
        errors.push({
          code: 'MISSING_METADATA',
          message: `Image ${index + 1} (${image.file?.name || image.id}) is missing metadata`,
          severity: 'error',
        })
        return
      }

      // Validate description length (skip for Adobe Stock as they use title instead)
      if (marketplace !== 'adobe-stock' && metadata.description && metadata.description.length > (requirements?.maxDescriptionLength || 2000)) {
        errors.push({
          code: 'DESCRIPTION_TOO_LONG',
          message: `Image ${index + 1}: Description exceeds ${requirements?.maxDescriptionLength || 2000} characters`,
          severity: 'error',
        })
      }

      // Validate keywords
      if (metadata.keywords.length > (requirements?.maxKeywords || 50)) {
        errors.push({
          code: 'TOO_MANY_KEYWORDS',
          message: `Image ${index + 1}: ${metadata.keywords.length} keywords exceed limit of ${requirements?.maxKeywords || 50}`,
          severity: 'error',
        })
      }

      if (metadata.keywords.length < 5) {
        warnings.push({
          code: 'FEW_KEYWORDS',
          message: `Image ${index + 1}: Only ${metadata.keywords.length} keywords (recommended: 5-15)`,
          severity: 'warning',
        })
      }

      // Validate title for marketplaces that require it
      if (requirements?.requiredColumns?.includes('Title') && (!metadata.title || metadata.title.length < 3)) {
        errors.push({
          code: 'INVALID_TITLE',
          message: `Image ${index + 1}: Title is required and must be at least 3 characters`,
          severity: 'error',
        })
      }

      // Validate title length for Adobe Stock
      if (marketplace === 'adobe-stock' && metadata.title && metadata.title.length > 200) {
        errors.push({
          code: 'TITLE_TOO_LONG',
          message: `Image ${index + 1}: Title exceeds 200 characters for Adobe Stock`,
          severity: 'error',
        })
      }

      // Check for model/property releases if required
      if (metadata.complianceFlags?.includes('MODEL_RELEASE_REQUIRED') && !metadata.hasModelRelease) {
        warnings.push({
          code: 'MISSING_MODEL_RELEASE',
          message: `Image ${index + 1}: Model release may be required`,
          severity: 'warning',
        })
      }

      if (metadata.complianceFlags?.includes('PROPERTY_RELEASE_REQUIRED') && !metadata.hasPropertyRelease) {
        warnings.push({
          code: 'MISSING_PROPERTY_RELEASE',
          message: `Image ${index + 1}: Property release may be required`,
          severity: 'warning',
        })
      }
    })

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
    }
  }

  async exportToCsv(batch: Batch, customColumns?: string[]): Promise<ExportResult> {
    try {
      const columns = customColumns || ['Filename', 'Title', 'Description', 'Keywords', 'Category']
      const csvContent = this.generateCustomCSV(batch, columns)
      const filename = `${batch.name || `batch-${batch.id}`}-custom-export.csv`

      return {
        marketplace: 'custom',
        filename,
        content: csvContent,
        rowCount: batch.images.length,
        exportedAt: new Date(),
        requirements: {
          requiredColumns: columns,
          optionalColumns: [],
          maxDescriptionLength: Infinity,
          maxKeywords: Infinity,
          keywordSeparator: ',',
          csvDelimiter: ',',
          requiresHeader: true,
          filenameFormat: 'original',
        },
      }
    } catch (error) {
      throw new ExportError(
        'Failed to export custom CSV',
        EXPORT_CODES.EXPORT_GENERATION_FAILED,
        'custom',
        { originalError: error instanceof Error ? error.message : String(error) },
      )
    }
  }

  async exportFromImages(images: any[], imageMetadata: Record<string, any>, marketplace: MarketplaceType, sessionName?: string): Promise<ExportResult> {
    try {
      console.log(`🔍 [CSVExporter] exportFromImages for ${marketplace}:`)
      console.log(`  - Images array:`, images)
      console.log(`  - Images count: ${images.length}`)
      console.log(`  - Image metadata:`, imageMetadata)
      console.log(`  - Session name: ${sessionName}`)

      const requirements = this.marketplaceRequirements[marketplace]

      // Create a temporary batch-like structure from images and metadata
      const tempBatch = {
        id: 'temp-export',
        name: sessionName || 'Current Session',
        createdAt: new Date(),
        images: images.map(image => ({
          id: image.id,
          file: image.file,
          metadata: imageMetadata[image.id]
        }))
      }

      console.log(`  - Temporary batch:`, tempBatch)

      // Validate the export
      const validation = this.validateExport(tempBatch, marketplace)
      if (!validation.isValid) {
        throw new ExportError(
          `Export validation failed: ${validation.errors.map(e => e.message).join(', ')}`,
          EXPORT_CODES.VALIDATION_FAILED,
          marketplace,
          { errors: validation.errors },
        )
      }

      const csvContent = this.generateCSVContent(tempBatch, marketplace)
      const filename = `${sessionName || 'Current Session'}-${marketplace}-export.csv`

      return {
        marketplace,
        filename,
        content: csvContent,
        rowCount: images.length,
        exportedAt: new Date(),
        requirements: requirements || {
          requiredColumns: ['Filename', 'Title', 'Keywords'],
          optionalColumns: [],
          maxDescriptionLength: 2000,
          maxKeywords: 50,
          keywordSeparator: ',',
          csvDelimiter: ',',
          requiresHeader: true,
          filenameFormat: 'original',
        },
      }
    } catch (error) {
      if (error instanceof ExportError) {
        throw error
      }
      throw new ExportError(
        'Failed to export from images',
        EXPORT_CODES.EXPORT_GENERATION_FAILED,
        marketplace,
        { originalError: error instanceof Error ? error.message : String(error) },
      )
    }
  }

  async downloadExport(exportResult: ExportResult): Promise<void> {
    try {
      const blob = new Blob([exportResult.content], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)

      const link = document.createElement('a')
      link.href = url
      link.download = exportResult.filename
      link.style.display = 'none'

      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      // Clean up the URL object
      setTimeout(() => URL.revokeObjectURL(url), 100)
    } catch (error) {
      throw new ExportError(
        'Failed to download export file',
        EXPORT_CODES.DOWNLOAD_FAILED,
        exportResult.marketplace,
        { originalError: error instanceof Error ? error.message : String(error) },
      )
    }
  }

  getMarketplaceRequirements(marketplace: MarketplaceType): MarketplaceRequirements {
    return { ...this.marketplaceRequirements[marketplace] }
  }

  getSupportedMarketplaces(): MarketplaceType[] {
    return Object.keys(this.marketplaceRequirements) as MarketplaceType[]
  }

  private generateCSVContent(batch: Batch, marketplace: MarketplaceType): string {
    const requirements = this.marketplaceRequirements[marketplace]
    const delimiter = requirements?.csvDelimiter || ','
    const lines: string[] = []

    // Debug logging
    console.log(`🔍 [CSVExporter] generateCSVContent for ${marketplace}:`)
    console.log(`  - Batch ID: ${batch?.id}`)
    console.log(`  - Batch name: ${batch?.name}`)
    console.log(`  - Images array:`, batch?.images)
    console.log(`  - Images count: ${batch?.images?.length || 0}`)
    console.log(`  - Requirements:`, requirements)

    // Add header if required
    if (requirements?.requiresHeader !== false) {
      const headerColumns = [...(requirements?.requiredColumns || []), ...(requirements?.optionalColumns || [])]
      lines.push(headerColumns.map(col => this.escapeCSVValue(col)).join(delimiter))
      console.log(`  - Header: ${lines[0]}`)
    }

    // Add data rows
    batch?.images?.forEach((image, index) => {
      console.log(`  - Processing image ${index + 1}:`, {
        id: image?.id,
        hasFile: !!image?.file,
        fileName: image?.file?.name,
        hasMetadata: !!image?.metadata,
        metadata: image?.metadata
      })

      const metadata = image.metadata
      if (!metadata) {
        console.log(`    ❌ Skipping image ${index + 1}: No metadata`)
        return
      }

      const row: string[] = []
      const allColumns = [...(requirements?.requiredColumns || []), ...(requirements?.optionalColumns || [])]

      allColumns.forEach(column => {
        const value = this.getColumnValue(image, column, marketplace)
        row.push(this.escapeCSVValue(value))
        console.log(`    - ${column}: "${value}"`)
      })

      lines.push(row.join(delimiter))
      console.log(`    ✅ Added row ${index + 1}: ${row.join(delimiter)}`)
    })

    const finalContent = lines.join('\n')
    console.log(`  - Final CSV content (${finalContent.length} characters):`)
    console.log(`"${finalContent}"`)

    return finalContent
  }

  private generateCustomCSV(batch: Batch, columns: string[]): string {
    const lines: string[] = []

    // Add header
    lines.push(columns.map(col => this.escapeCSVValue(col)).join(','))

    // Add data rows
    batch.images.forEach(image => {
      const metadata = image.metadata
      if (!metadata) return

      const row: string[] = []
      columns.forEach(column => {
        const value = this.getColumnValue(image, column, 'custom')
        row.push(this.escapeCSVValue(value))
      })

      lines.push(row.join(','))
    })

    return lines.join('\n')
  }

  private getColumnValue(image: any, column: string, marketplace: MarketplaceType): string {
    const metadata = image.metadata
    const requirements = marketplace !== 'custom' ? this.marketplaceRequirements[marketplace] : null

    // Normalize column name for case-insensitive matching
    const normalizedColumn = column.toLowerCase().replace(/[^a-z]/g, '')

    switch (normalizedColumn) {
      case 'filename':
      case 'file':
      case 'name':
        return this.formatFilename(image.file?.name || `image-${image.id}.jpg`, requirements?.filenameFormat || 'original')

      case 'title':
        // For Adobe Stock, title serves as description (up to 200 chars)
        if (marketplace === 'adobe-stock') {
          // Use description if available, otherwise title, truncate to 200 chars
          const content = metadata?.description || metadata?.title || ''
          return content.length > 200 ? content.substring(0, 197) + '...' : content
        }
        return metadata?.title || ''

      case 'description':
        return this.truncateDescription(metadata?.description || '', requirements?.maxDescriptionLength || 2000)

      case 'keywords':
      case 'keyword':
        return this.formatKeywords(metadata?.keywords || [], requirements)

      case 'category':
      case 'categories':
        // For Adobe Stock, return category number instead of text
        if (marketplace === 'adobe-stock') {
          // Adobe Stock uses numeric categories - map common categories to numbers
          const categoryMapping: Record<string, string> = {
            'People': '1',
            'Animals': '2', 
            'Buildings and Architecture': '3',
            'Business/Finance': '4',
            'Education': '5',
            'Food and Drink': '6',
            'Healthcare/Medical': '7',
            'Hobbies and Leisure': '8',
            'Industrial': '9',
            'Landscapes': '10',
            'Lifestyle': '11',
            'Nature': '12',
            'Objects': '13',
            'Science': '14',
            'Technology': '15',
            'Transportation': '16',
            'Travel': '17',
            'Vintage': '18',
            'Arts Culture and Entertainment': '19',
            'Beauty/Fashion': '20'
          }
          return categoryMapping[metadata?.category || ''] || '1' // Default to People
        }
        return metadata?.category || ''

      case 'editorial':
        return metadata?.isCommercial === false ? 'Yes' : 'No'

      case 'modelrelease':
      case 'modelreleased':
        // Adobe Stock uses "Yes"/"No", others might use different formats
        return metadata?.hasModelRelease ? 'Yes' : 'No'

      case 'propertyrelease':
      case 'propertyreleased':
        // Adobe Stock uses "Yes"/"No", others might use different formats
        return metadata?.hasPropertyRelease ? 'Yes' : 'No'

      case 'maturecontent':
      case 'mature':
        // Check if image contains mature content based on compliance flags or category
        const hasMatureContent = metadata?.complianceFlags?.includes('SENSITIVE_CONTENT') || 
                                metadata?.category?.toLowerCase().includes('adult') ||
                                metadata?.keywords?.some(k => ['nude', 'adult', 'mature'].includes(k.toLowerCase()))
        return hasMatureContent ? 'yes' : 'no'

      case 'illustration':
        // Use AI analysis if available for accurate style detection
        if (metadata?.analysis?.style) {
          // AI provides reliable style classification
          return metadata.analysis.style === 'illustration' || metadata.analysis.style === 'graphic' ? 'yes' : 'no'
        }
        
        // Fallback: Much more conservative illustration detection - only mark as illustration if explicitly indicated
        // Photography should never be marked as illustration unless it's clearly digital art/vector/drawing
        const isIllustration = metadata?.keywords?.some(k => {
          const keyword = k.toLowerCase()
          return ['illustration', 'vector', 'cartoon', 'digital art', 'drawing', 'graphic design', 'artwork'].includes(keyword)
        }) && !metadata?.keywords?.some(k => {
          const keyword = k.toLowerCase()
          // If it has photography-related keywords, it's likely photography
          return ['photography', 'photo', 'photograph', 'camera', 'lens', 'shot', 'captured', 'real', 'natural', 'candid'].includes(keyword)
        })
        return isIllustration ? 'yes' : 'no'

      case 'releases':
        // Adobe Stock specific: Combine model and property releases as names
        const releases = []
        if (metadata?.hasModelRelease) {
          releases.push('Model Release Available')
        }
        if (metadata?.hasPropertyRelease) {
          releases.push('Property Release Available')
        }
        return releases.join(', ')

      default:
        return ''
    }
  }

  private formatFilename(filename: string, format: string): string {
    switch (format) {
      case 'no-extension':
        return filename.replace(/\.[^.]*$/, '')
      case 'original':
      default:
        return filename
    }
  }

  private formatKeywords(keywords: string[], requirements?: MarketplaceRequirements): string {
    if (!keywords || keywords.length === 0) return ''

    const separator = requirements?.keywordSeparator || ','
    const maxKeywords = requirements?.maxKeywords || keywords.length

    return keywords.slice(0, maxKeywords).join(separator)
  }

  private truncateDescription(description: string, maxLength?: number): string {
    if (!maxLength || description.length <= maxLength) {
      return description
    }

    return `${description.substring(0, maxLength - 3)}...`
  }

  private escapeCSVValue(value: string): string {
    if (!value) return ''

    // If the value contains comma, quote, or newline, wrap in quotes and escape quotes
    if (value.includes(',') || value.includes('"') || value.includes('\n') || value.includes('\r')) {
      return `"${value.replace(/"/g, '""')}"`
    }

    return value
  }
}
